import { useEffect, useRef, useState } from "react";
import api from "../lib/apiClient";

// All canvas + scratch + reward logic for ScratchCard, so the component stays presentational.
// The reward is decided by the BACKEND on mount (idempotent /wallet/scratch); the canvas only reveals it.
export function useScratchCard(token, onReveal) {
  const canvasRef = useRef(null);
  const drawing = useRef(false);
  const cleared = useRef(false);
  const strokes = useRef(0);
  const called = useRef(false); // strict single-fire latch (guards React.StrictMode double-mount)
  const [amount, setAmount] = useState(null);
  const [resultType, setResultType] = useState("CASHBACK");
  const [ticket, setTicket] = useState(null);
  const [status, setStatus] = useState("loading"); // loading | ready | revealed | error
  const [errMsg, setErrMsg] = useState("");
  const [sparks, setSparks] = useState([]);

  // 1) Finalize reward on the server before the scratch begins. Fires EXACTLY once per mount.
  useEffect(() => {
    if (called.current) return;
    called.current = true;
    (async () => {
      try {
        const { data } = await api.post("/wallet/scratch", { tokenId: token.tokenId });
        setAmount(data.revealed_paise);
        setResultType(data.result_type || "CASHBACK");
        setTicket(data.ticket || null);
        setStatus("ready");
      } catch (e) {
        setErrMsg(e?.response?.data?.detail || "Unable to process this token. Please try again.");
        setStatus("error");
      }
    })();
  }, [token.tokenId]);

  // 2) Paint the holographic coating once we know the reward exists.
  useEffect(() => {
    if (status !== "ready") return;
    paintCoating(canvasRef.current);
  }, [status]);

  const pos = (e) => {
    const r = canvasRef.current.getBoundingClientRect();
    return { x: e.clientX - r.left, y: e.clientY - r.top };
  };

  const scratchAt = (x, y) => {
    const ctx = canvasRef.current.getContext("2d");
    const rad = 20 + Math.random() * 8;
    ctx.fillStyle = "rgba(0,0,0,1)"; // destination-out uses source alpha; full alpha = clean erase
    ctx.beginPath(); ctx.arc(x, y, rad, 0, Math.PI * 2); ctx.fill();
    if (navigator.vibrate) navigator.vibrate(4);
    if (Math.random() > 0.45) { // golden glitter particles under the finger
      const id = Date.now() + Math.random();
      const dx = (Math.random() - 0.5) * 26, dy = (Math.random() - 0.5) * 26;
      setSparks((s) => [...s.slice(-14), { id, x, y, dx, dy }]);
      setTimeout(() => setSparks((s) => s.filter((p) => p.id !== id)), 650);
    }
  };

  const finish = () => {
    if (cleared.current) return;
    cleared.current = true;
    const cv = canvasRef.current;
    cv.style.transition = "opacity .45s ease";
    cv.style.opacity = "0";
    if (navigator.vibrate) navigator.vibrate([10, 30, 10]);
    setTimeout(() => { setStatus("revealed"); onReveal && onReveal(amount, resultType); }, 450);
  };

  const checkCleared = () => {
    if (cleared.current) return;
    const cv = canvasRef.current, ctx = cv.getContext("2d", { willReadFrequently: true });
    const { data } = ctx.getImageData(0, 0, cv.width, cv.height);
    let clear = 0, total = 0;
    for (let i = 3; i < data.length; i += 40) { total++; if (data[i] < 40) clear++; }
    if (total && clear / total > 0.5) finish(); // auto-reveal once ~half is scratched
  };

  const down = (e) => { drawing.current = true; e.currentTarget.setPointerCapture?.(e.pointerId); const p = pos(e); scratchAt(p.x, p.y); };
  const move = (e) => { if (!drawing.current) return; const p = pos(e); scratchAt(p.x, p.y); strokes.current++; if (strokes.current % 3 === 0) checkCleared(); };
  const up = () => { drawing.current = false; checkCleared(); };

  const won = resultType === "CASHBACK" && amount > 0;
  const isTicket = resultType === "LUCKY_TICKET";
  const positive = won || isTicket;

  return { canvasRef, status, errMsg, amount, resultType, ticket, sparks,
           won, isTicket, positive, down, move, up, finish };
}

function paintCoating(cv) {
  if (!cv) return;
  const ctx = cv.getContext("2d");
  const w = (cv.width = cv.offsetWidth), h = (cv.height = cv.offsetHeight);
  const g = ctx.createLinearGradient(0, 0, w, h);
  g.addColorStop(0, "#f97316"); g.addColorStop(0.25, "#f43f5e"); g.addColorStop(0.5, "#a855f7");
  g.addColorStop(0.75, "#06b6d4"); g.addColorStop(1, "#f59e0b"); // holographic rainbow foil
  ctx.fillStyle = g; ctx.fillRect(0, 0, w, h);
  for (let i = 0; i < w * h * 0.04; i++) { // glitter texture
    ctx.fillStyle = `rgba(255,${Math.random() > 0.5 ? 240 : 180},${Math.random() > 0.5 ? 200 : 90},0.12)`;
    ctx.fillRect(Math.random() * w, Math.random() * h, 1.5, 1.5);
  }
  ctx.save(); // branded foil: faint repeating PARAS watermark
  ctx.globalAlpha = 0.12; ctx.fillStyle = "#ffffff"; ctx.font = "700 12px 'JetBrains Mono',monospace";
  for (let y = 16; y < h; y += 30) {
    for (let x = ((y / 30) % 2 ? 20 : -20); x < w; x += 90) ctx.fillText("PARAS", x, y);
  }
  ctx.restore();
  ctx.fillStyle = "rgba(255,255,255,0.95)"; ctx.textAlign = "center"; ctx.font = "700 15px 'Plus Jakarta Sans',sans-serif";
  ctx.fillText("✨ SCRATCH HERE ✨", w / 2, h / 2 - 6);
  ctx.font = "600 10px 'JetBrains Mono',monospace"; ctx.fillStyle = "rgba(255,255,255,0.7)";
  ctx.fillText("PARAS CASHBACK", w / 2, h / 2 + 16);
  ctx.globalCompositeOperation = "destination-out";
}
